import { Flex, Link, Typography, useModal } from '@aloft/toolkit-sui';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectEditablePropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/editablePropertyDataSelectors.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { schAppraisifyStopAndCall } from './schemas.ts';
import { StopAndCallIssue } from './types.ts';
import { Textarea } from '@aloft/toolkit-sui/chakra';
import { resetPropertyIdentificationValue } from '../../redux/features/actions/resetPropertyIdentificationValue.ts';
import { resetImprovementsValue } from '../../redux/features/actions/resetImprovementsValue.ts';
import { resetReconciliationValue } from '../../redux/features/reconciliationSlice.ts';
import { fetchEscalateTkv2OrderAPI } from '../api/toolkitComponents.ts';
import { EscalationType } from '@aloft/toolkit-shared';
import { useState } from 'react';
import { useToolkitContext } from '../api/toolkitContext.ts';
import { ESCALATION_TYPE_SENTENCES, STOP_AND_CALL_FIELD_NAME } from './consts.ts';
import { useNavigate } from 'react-router';
import { bypassEscalation } from '../../redux/features/uiSlice.ts';
import { selectEscalationBypassed } from '../../redux/features/selectors/uiSelectors.ts';
import { getAPIEscalationType } from './utils.ts';

export const useAppraisifyStopAndCall = () => {
  const order = useAppSelector(selectOrder);
  const subjectData = useAppSelector((state) => selectEditablePropertyDataById(state, 'subject'));
  const escalationBypassed = useAppSelector(selectEscalationBypassed);
  const reconciliation = useAppSelector(selectReconciliation);
  const dispatch = useAppDispatch();
  const context = useToolkitContext();
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { fetcherOptions } = context;
  const navigate = useNavigate();

  const validationForm = schAppraisifyStopAndCall.safeParse({
    orderDetails: {
      zoningCompliance: subjectData?.propertyIdentification.zoningCompliance,
      propertyInterest: subjectData?.propertyIdentification.propertyInterest,
    },
    features: {
      subject: {
        uadCondition: subjectData?.improvements.uadCondition,
      },
    },
    reconciliation: {
      valueTypes: reconciliation.valueTypes,
      allowSubjectToValue: reconciliation.allowSubjectToValue,
    },
  });



  const getStopAndCallIssue = (): StopAndCallIssue | undefined => {
    if (!order?.isInspectifyOrder) return undefined;

    let issues: (StopAndCallIssue | undefined)[] = [];

    if (validationForm.error) {
      issues = validationForm.error.issues
        .map((issue) => {
          const { path, code, message } = issue;
          const fieldName: STOP_AND_CALL_FIELD_NAME = (path[path.length - 1]?.toString() as STOP_AND_CALL_FIELD_NAME);

          if (code !== 'custom' || !fieldName) return undefined;

          if (fieldName === 'propertyInterest') {
            const escalationType = getAPIEscalationType(fieldName, subjectData?.propertyIdentification.propertyInterest);
            if (escalationType === order.escalationType && !order.isEscalated) {
              return undefined;
            }
            return {
              title: `You have indicated the Subject's property interest is ${subjectData?.propertyIdentification.propertyInterest?.toUpperCase()}.`,
              descrition: message,
              fieldName,
              apiEscalationType: escalationType,
            };
          }
          if (fieldName === 'zoningCompliance') {
            const escalationType = getAPIEscalationType(fieldName, subjectData?.propertyIdentification.zoningCompliance);
            if (escalationType === order.escalationType && !order.isEscalated) {
              return undefined;
            }
            return {
              title: `You have indicated the Subject's zoning compliance is ${subjectData?.propertyIdentification.zoningCompliance?.toUpperCase()}.`,
              descrition: message,
              fieldName,
              apiEscalationType: escalationType,
            };
          }
          if (fieldName === 'uadCondition') {
            if (order.hasArvOption) return undefined;
            const escalationType = getAPIEscalationType(fieldName, subjectData?.improvements.uadCondition);
            if (escalationType === order.escalationType && !order.isEscalated) {
              return undefined;
            }
            return {
              title: `You have indicated the Subject's condition rating as a C${subjectData?.improvements.uadCondition}.`,
              descrition: message,
              justification: `Please explain what specific defects and conditions makes this property a C${subjectData?.improvements.uadCondition}:`,
              fieldName,
              apiEscalationType: escalationType,
            };
          }
          if (fieldName === 'valueTypes') {
            const escalationType = getAPIEscalationType(fieldName, reconciliation.valueTypes);
            if (escalationType === order.escalationType && !order.isEscalated) {
              return undefined;
            }
            return {
              title: `You have selected “Subject-To.”`,
              descrition: message,
              justification:
                'Please explain why an As-Is appraisal cannot be completed and the specific condition(s) driving this determination. To review your report and confirm Subject-To is the best option, click Cancel.',
              fieldName,
              apiEscalationType: escalationType,
            };
          }

          return undefined;
        })
        .filter(Boolean);
    }

    return issues[0];
  };

  const currentIssue = getStopAndCallIssue();

  const onCancel = (issue: StopAndCallIssue) => {
    if (!issue?.fieldName) return;
    if (['propertyInterest', 'zoningCompliance'].includes(issue.fieldName)) {
      dispatch(resetPropertyIdentificationValue({ id: 'subject', fieldName: issue.fieldName }))
    }
    if (['uadCondition'].includes(issue.fieldName)) {
      dispatch(resetImprovementsValue({ id: 'subject', fieldName: issue.fieldName }))
    }
    if (['valueTypes'].includes(issue.fieldName)) {
      dispatch(resetReconciliationValue({ fieldName: issue.fieldName, resetValue: [] }))
    }
  };

  const onReturnToOrders = () => {
    navigate('/orders', { replace: true });
  };

  const onBypass = () => {
    dispatch(bypassEscalation());
  };

  const onEscalate = (issue: StopAndCallIssue, escalationReason: string) => {
    setIsSubmitting(true);
    fetchEscalateTkv2OrderAPI({
      ...fetcherOptions,
      pathParams: { orderID: order.id },
      body: {
        escalationType: issue.apiEscalationType,
        escalationReason
      }
    }).then(() => {
      setIsSubmitted(true);
      setIsSubmitting(false);
    }).catch((e) => {
      setIsSubmitting(false);
      setIsSubmissionError(true);
      console.error(e);
      throw e;
    })
  }

  const stopAndCallModal = () => {
    const { Modal } = useModal();
    const [escalationReason, setEscalationReason] = useState('');

    if (escalationBypassed) {
      return null;
    }

    if (isSubmitted || order.isEscalated) {
      return (
        <Modal
          isOpen={true}
          title="Order has been escalated"
          showCloseButton={false}
          primaryAction={{
            text: "View order",
            onClick: onBypass,
            variant: 'primary'
          }}
          secondaryAction={{
            text: "Close",
            onClick: onReturnToOrders,
            variant: 'secondary'
          }}
          onClose={() => { }}
        >
          <Flex flexDirection='column' gap={2}>
            <Typography color="textLight" variant="body1">This order is escalated for the following reason:</Typography>
            <Typography color="textLight" variant="h4">{ESCALATION_TYPE_SENTENCES[currentIssue?.apiEscalationType || order.escalationType!]}</Typography>
            <ul>
              <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
                <Typography variant="body1" color="textSecondary">You will not be able to certify this order while Appraisify escalates the issue to our client.</Typography>
              </li>
              <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
                <Typography variant="body1" color="textSecondary">We will follow up with guidance on whether the order should proceed, be modified, or be canceled.</Typography>
              </li>
              <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
                <Typography variant="body1" color="textSecondary">If the order is cancelled, you will receive a fee.</Typography>
              </li>
            </ul>
          </Flex>
        </Modal>
      )
    }

    if (!currentIssue) return null;

    return (
      <Modal
        isOpen={!!currentIssue}
        onClose={() => { }}
        showCloseButton={false}
        primaryAction={{
          onClick: () => { onEscalate(currentIssue, escalationReason) },
          text: 'Escalate',
          variant: 'danger',
          disabled: isSubmissionError || !!(!escalationReason && currentIssue.justification) || isSubmitting,
          isLoading: isSubmitting
        }}
        secondaryAction={{
          onClick: () => onCancel(currentIssue),
          text: 'Cancel',
          variant: 'secondary',
          disabled: isSubmitting
        }}
        title="Stop and Call Required"
      >
        <Flex flexDirection='column' gap={2}>
          {isSubmissionError ? (
            <Typography color="textError" variant="h4">Uh oh, something went wrong while escalating the appraisal.
              Please reach out directly to <Link href="mailto:orders@appraisify.com" color="buttonDefault">
                orders@appraisify.com
              </Link>.
            </Typography>
          ) : (
            <>
              <Typography color="textLight" variant="h4">{currentIssue.title}</Typography>
              {currentIssue.descrition.split('\n').map((line, index) => (
                <Typography key={index}>{line}</Typography>
              ))}
              {currentIssue.justification && (
                <>
                  <Typography color="textLight" variant="h4">{currentIssue.justification}</Typography>
                  <Textarea required value={escalationReason} onChange={(e) => setEscalationReason(e.target.value)} />
                  <Typography variant="h4">NOTE: This will be relayed directly to our client.</Typography>
                </>
              )}
            </>
          )
          }

        </Flex >
      </Modal >
    );
  };

  return {
    issue: currentIssue,
    stopAndCallModal,
  };
};
